import React from "react";
import { Box, Typography, IconButton, Divider } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAppDispatch } from "@store/index";
import { donutChartConfigDispatcher } from "@store/slices/donut-chart-config-slice";
import HPDropdown from "../common/Dropdown";
import HPInput from "../common/Input";
import IOSSwitch from "../common/Switch";
import { InfoOutline } from "@mui/icons-material";

interface SliceGroupComponentProps {
  group: {
    id: string;
    name: string;
    sliceNames: string[];
    showAsSlice: boolean;
    isHidden: boolean;
  };
  availableSlices: string[];
  usedSlices: string[]; // Slices already used in other groups
  onRemove: (groupId: string) => void;
}

const SliceGroupComponent: React.FC<SliceGroupComponentProps> = ({
  group,
  availableSlices,
  usedSlices,
  onRemove,
}) => {
  const dispatch = useAppDispatch();
  const { updateGroup } = donutChartConfigDispatcher(dispatch);

  // Get slices that can be selected (not used in other groups)
  const selectableSlices = availableSlices.filter(
    (slice) => !usedSlices.includes(slice) || group.sliceNames.includes(slice),
  );

  const handleSliceSelectionChange = (value: string[]) => {
    updateGroup({
      id: group.id,
      updates: { sliceNames: value },
    });
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateGroup({
      id: group.id,
      updates: { name: event.target.value },
    });
  };

  const handleShowAsSliceChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    updateGroup({
      id: group.id,
      updates: { showAsSlice: event.target.checked },
    });
  };

  const handleHiddenGroupChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    updateGroup({
      id: group.id,
      updates: { isHidden: event.target.checked },
    });
  };

  const isValidGroup = group.sliceNames.length >= 2;

  return (
    <Box>
      {/* Header with delete button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="subtitle2" fontWeight={600}>
          Group slices
        </Typography>
        <IconButton size="small" onClick={() => onRemove(group.id)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Slice Selection */}
      <Box sx={{ mb: 2 }}>
        <HPDropdown
          label="Select slices to group"
          value={group.sliceNames}
          onChange={handleSliceSelectionChange}
          options={selectableSlices.map((slice) => ({
            value: slice,
            label: slice,
          }))}
          multiSelect={true}
          placeholder="Choose slices to group"
          error={!isValidGroup && group.sliceNames.length > 0}
        />
        {!isValidGroup && group.sliceNames.length > 0 && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
            Groups must have at least 2 slices
          </Typography>
        )}
      </Box>

      {/* Group Name */}
      <HPInput
        label="Group name"
        value={group.name}
        onChange={handleNameChange}
        className="mb-2"
        placeholder="Enter group name"
        error={isValidGroup && !group.name.trim()}
        helperText={
          isValidGroup && !group.name.trim() ? "Group name is required" : ""
        }
      />

      {/* Show as Slice Toggle */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 2 }}>
          <IOSSwitch
            checked={group.showAsSlice}
            onChange={handleShowAsSliceChange}
            size="small"
            className="mr-1"
          />
          <Typography variant="body2">Show as slice</Typography>
          <InfoOutline sx={{ fontSize: 16, color: "text.secondary" }} />
        </Box>
      </Box>

      {/* Hidden Group Toggle */}
      {/* <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="body2">Hidden group</Typography>
          <InfoIcon sx={{ fontSize: 16, color: "text.secondary" }} />
        </Box>
        <IOSSwitch
          checked={group.isHidden}
          onChange={handleHiddenGroupChange}
          size="small"
        />
      </Box> */}

      <Divider sx={{ mt: 4 }} />
    </Box>
  );
};

export default SliceGroupComponent;
